import React, { useState } from "react";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";

// 🔐 Basic attack pattern detection
const containsMaliciousPattern = (value) => {
  if (!value) return false;

  const lower = value.toLowerCase().trim();

  // Excel / CSV Injection
  if (/^[=+\-@]/.test(lower)) return true;

  // Script tags or HTML tags
  if (/<[^>]*>/g.test(lower)) return true;

  // javascript protocol
  if (lower.includes("javascript:")) return true;

  // onerror, onclick, etc
  if (/on\w+=/g.test(lower)) return true;

  return false;
};

// Optional: Clean value (strip HTML)
const sanitizeValue = (value) => {
  return value.replace(/<[^>]*>?/gm, "");
};

const InputBase = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  error = "",
  required = false,
  disabled = false,
  showPasswordToggle = false,
  className = "",
  rows = 4,
  variant = "default", // "default" | "location"
  onChangeLocation,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";
  const isTextarea = type === "textarea";
  const isLocation = variant === "location";

  const handleSecureChange = (e) => {
    let originalValue = e.target.value;

    // Remove HTML tags
    let cleanedValue = originalValue.replace(/<[^>]*>?/gm, "");

    // Prevent Excel/CSV injection if first character
    if (/^[=+\-@]/.test(cleanedValue)) {
      cleanedValue = cleanedValue.slice(1);
    }

    // Remove javascript:
    cleanedValue = cleanedValue.replace(/javascript:/gi, "");

    // ❌ Remove starting spaces only
    cleanedValue = cleanedValue.trimStart();

    onChange({
      target: {
        name,
        value: cleanedValue,
      },
    });
  };

  return (
    <div className="w-full flex flex-col gap-1">
      {/* Label */}
      {label && (
        <label htmlFor={name} className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      <div className="relative">
        {/* 🔹 LOCATION TEXTAREA */}
        {isTextarea && isLocation ? (
          <div
            className={`relative w-full rounded-xl border p-4 bg-white
              ${error ? "border-red-500" : "border-gray-300"}
              ${disabled ? "bg-gray-100" : ""}
              ${className}
            `}
          >
            {/* Location Icon */}
            <MdLocationOn
              className="absolute top-4 left-4 text-[#FB6742]"
              size={20}
            />

            {/* Textarea */}
            <textarea
              id={name}
              name={name}
              value={value}
              onChange={onChange}
              disabled={disabled}
              rows={rows}
              className="
                w-full pl-10 pr-20 text-sm sm:text-base resize-none
                focus:outline-none bg-transparent
              "
            />

            {/* CHANGE link */}
            <button
              type="button"
              onClick={onChangeLocation}
              className="absolute bottom-4 right-4 text-xs font-medium text-[#FB6742]"
            >
              CHANGE
            </button>
          </div>
        ) : isTextarea ? (
          /* 🔹 NORMAL TEXTAREA */
          <textarea
            id={name}
            name={name}
            value={value}
            onChange={handleSecureChange}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            className={`
              w-full px-4 py-2 rounded-lg border
              text-sm sm:text-base resize-none
              focus:outline-none
              ${error ? "border-red-500" : "border-gray-300"}
              ${disabled ? "bg-gray-100" : "bg-white"}
              ${className}
            `}
          />
        ) : (
          /* 🔹 INPUT */
          <input
            id={name}
            name={name}
            type={
              isPassword && showPasswordToggle && showPassword ? "text" : type
            }
            value={value}
            onChange={handleSecureChange}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              w-full px-4 py-2 rounded-lg border pr-11
              text-sm sm:text-base
              focus:outline-none
              ${error ? "border-red-500" : "border-gray-300"}
              ${disabled ? "bg-gray-100" : "bg-white"}
              ${className}
            `}
          />
        )}

        {/* Password toggle */}
        {isPassword && showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
          >
            {showPassword ? (
              <FaRegEyeSlash size={20} />
            ) : (
              <FaRegEye size={20} />
            )}
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default InputBase;
